import { useQuery } from 'react-query';

import { fetchKommuneCenterPoint, searchAddressByMatrikkel } from '../../../../../services/api';
import { hasValidCoordinates } from '../utils/coordinates';

export type GeoLocationStatus = 'idle' | 'found' | 'multiple' | 'not-found' | 'loading' | 'error';

export type GeoLocationInput = {
  kommunenummer?: string;
  gardsnummer?: string;
  bruksnummer?: string;
  adressenavn?: string;
  nummer?: string;
  bokstav?: string;
};

type RequiredGeoLocationInput = GeoLocationInput & {
  kommunenummer: string;
  gardsnummer: string;
  bruksnummer: string;
};

type GeoLocationResult = {
  latitude?: number;
  longitude?: number;
  status: Exclude<GeoLocationStatus, 'loading' | 'idle'>;
  hitCount: number;
};

const hasRequiredMatrikkelInput = (input?: GeoLocationInput): input is RequiredGeoLocationInput =>
  !!(input?.kommunenummer && input?.gardsnummer && input?.bruksnummer);

export const useGeoLocation = (input?: GeoLocationInput) => {
  const hasRequiredInput = hasRequiredMatrikkelInput(input);

  const { data, isLoading } = useQuery(
    [
      'solceller-geo-location',
      input?.kommunenummer,
      input?.gardsnummer,
      input?.bruksnummer,
      input?.adressenavn,
      input?.nummer,
      input?.bokstav
    ],
    async (): Promise<GeoLocationResult> => {
      if (!hasRequiredMatrikkelInput(input)) {
        return { status: 'error', hitCount: 0 };
      }

      const addressSearch = await searchAddressByMatrikkel({
        kommunenummer: input.kommunenummer,
        gardsnummer: input.gardsnummer,
        bruksnummer: input.bruksnummer,
        adressenavn: input.adressenavn,
        nummer: input.nummer,
        bokstav: input.bokstav
      });

      const addressesWithCoordinates = (addressSearch?.adresser || []).filter(
        (address) => hasValidCoordinates(address.representasjonspunkt?.lat, address.representasjonspunkt?.lon)
      );

      if (addressesWithCoordinates.length > 0) {
        const firstAddress = addressesWithCoordinates[0];
        const isMultipleHits = addressesWithCoordinates.length > 1;

        return {
          latitude: firstAddress.representasjonspunkt?.lat,
          longitude: firstAddress.representasjonspunkt?.lon,
          status: isMultipleHits ? 'multiple' : 'found',
          hitCount: addressesWithCoordinates.length
        };
      }

      const kommuneCenter = await fetchKommuneCenterPoint(input.kommunenummer);

      if (!kommuneCenter) {
        return { status: 'error', hitCount: 0 };
      }

      return {
        latitude: kommuneCenter.latitude,
        longitude: kommuneCenter.longitude,
        status: 'not-found',
        hitCount: 0
      };
    },
    {
      enabled: hasRequiredInput,
      refetchOnWindowFocus: false
    }
  );

  const status: GeoLocationStatus = !hasRequiredInput ? 'idle' : isLoading ? 'loading' : (data?.status ?? 'error');

  return {
    latitude: data?.latitude,
    longitude: data?.longitude,
    hitCount: data?.hitCount ?? 0,
    status,
    isLoading
  };
};
